/**
 * usePhotoUpload Hook
 * 
 * Handles photo uploads with metadata extraction.
 */

import { useState, useCallback } from "react";
import * as ImagePicker from "expo-image-picker";
import * as FileSystem from "expo-file-system/legacy";
import { Alert } from "react-native";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { logActivity } from "@/lib/activity-log";
import type { PhotoUploadData, PhotoUploadProgress } from "../types";

/**
 * Parse EXIF date format (YYYY:MM:DD HH:MM:SS) to ISO string
 * Returns undefined if parsing fails
 */
function parseExifDate(exifDate: string | undefined): string | undefined {
  if (!exifDate) return undefined;
  
  try {
    // EXIF format: "YYYY:MM:DD HH:MM:SS"
    // Convert to: "YYYY-MM-DDTHH:MM:SS"
    const match = exifDate.match(/^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
    if (match) {
      const [, year, month, day, hour, minute, second] = match;
      const isoString = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
      const date = new Date(isoString);
      
      // Validate the date is valid
      if (isNaN(date.getTime())) {
        return undefined;
      }
      
      return date.toISOString();
    }
    
    // Try parsing as-is (might be already in a standard format)
    const date = new Date(exifDate);
    if (isNaN(date.getTime())) {
      return undefined;
    }
    
    return date.toISOString();
  } catch {
    return undefined;
  }
}

interface UsePhotoUploadReturn {
  isUploading: boolean;
  progress: PhotoUploadProgress[];
  pickPhotos: () => Promise<PhotoUploadData[]>;
  takePhoto: () => Promise<PhotoUploadData | null>;
  uploadPhoto: (data: PhotoUploadData) => Promise<string | null>;
  uploadMultiple: (photos: PhotoUploadData[]) => Promise<string[]>;
  cancelUpload: () => void;
}

/**
 * Hook to handle photo uploads
 */
export const usePhotoUpload = (): UsePhotoUploadReturn => {
  const { user, activeProfile, userTeam, activeWeekend } = useAuth();
  const userId = activeProfile?.id || user?.id;
  
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<PhotoUploadProgress[]>([]);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  /**
   * Pick photos from gallery
   */
  const pickPhotos = useCallback(async (): Promise<PhotoUploadData[]> => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== "granted") {
        throw new Error("Geen toegang tot foto's");
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"], // New API format instead of deprecated MediaTypeOptions
        allowsMultipleSelection: true,
        quality: 0.8, // Compress on pick
        exif: true,
      });

      if (result.canceled || !result.assets) {
        return [];
      }

      return result.assets.map(asset => ({
        uri: asset.uri,
        fileName: asset.fileName || `photo_${Date.now()}.jpg`,
        mimeType: asset.mimeType || "image/jpeg",
        width: asset.width,
        height: asset.height,
        weekendId: activeWeekend?.id,
        teamId: userTeam?.id,
        activityType: "general",
        // Extract EXIF data if available (safely parse the date)
        takenAt: parseExifDate(asset.exif?.DateTimeOriginal as string | undefined),
        location: asset.exif?.GPSLatitude && asset.exif?.GPSLongitude
          ? {
              latitude: asset.exif.GPSLatitude,
              longitude: asset.exif.GPSLongitude,
            }
          : undefined,
      }));
    } catch (err) {
      console.error("Error picking photos:", err);
      return [];
    }
  }, [activeWeekend?.id, userTeam?.id]);

  /**
   * Take a photo with camera
   */
  const takePhoto = useCallback(async (): Promise<PhotoUploadData | null> => {
    try {
      const { status } = await ImagePicker.requestCameraPermissionsAsync();
      if (status !== "granted") {
        throw new Error("Geen toegang tot camera");
      }

      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ["images"], // New API format instead of deprecated MediaTypeOptions
        quality: 0.8, // Compress on capture
        exif: true,
      });

      if (result.canceled || !result.assets?.[0]) {
        return null;
      }

      const asset = result.assets[0];
      return {
        uri: asset.uri,
        fileName: asset.fileName || `photo_${Date.now()}.jpg`,
        mimeType: asset.mimeType || "image/jpeg",
        width: asset.width,
        height: asset.height,
        weekendId: activeWeekend?.id,
        teamId: userTeam?.id,
        activityType: "general",
        takenAt: new Date().toISOString(),
      };
    } catch (err) {
      console.error("Error taking photo:", err);
      return null;
    }
  }, [activeWeekend?.id, userTeam?.id]);

  /**
   * Upload a single photo
   */
  const uploadPhoto = useCallback(async (data: PhotoUploadData): Promise<string | null> => {
    if (!userId) return null;

    const photoId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    try {
      // Update progress
      setProgress(prev => [...prev, {
        photoId,
        progress: 0,
        status: "processing",
      }]);

      setProgress(prev => prev.map(p =>
        p.photoId === photoId ? { ...p, progress: 20, status: "uploading" } : p
      ));

      // Get file info
      const fileInfo = await FileSystem.getInfoAsync(data.uri);
      const fileSize = (fileInfo as any).size || 0;

      // Generate unique filename
      const timestamp = Date.now();
      const random = Math.random().toString(36).substr(2, 9);
      const weekendPath = data.weekendId || "no-weekend";
      const activityPath = data.activityType || "general";
      const fileName = `${weekendPath}/${activityPath}/${userId}_${timestamp}_${random}.jpg`;

      // Read file as base64
      const base64 = await FileSystem.readAsStringAsync(data.uri, {
        encoding: FileSystem.EncodingType.Base64,
      });

      setProgress(prev => prev.map(p =>
        p.photoId === photoId ? { ...p, progress: 50 } : p
      ));

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from("family-gallery")
        .upload(fileName, decode(base64), {
          contentType: "image/jpeg",
          upsert: false,
        });

      if (uploadError) throw uploadError;

      setProgress(prev => prev.map(p =>
        p.photoId === photoId ? { ...p, progress: 80 } : p
      ));

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("family-gallery")
        .getPublicUrl(fileName);

      // Insert record in database
      const { data: photoRecord, error: dbError } = await supabase
        .from("files")
        .insert({
          uploaded_by: userId,
          weekend_id: data.weekendId || null,
          team_id: data.teamId || null,
          name: fileName,
          original_name: data.fileName || fileName,
          file_url: urlData.publicUrl,
          file_type: "image/jpeg",
          file_size: fileSize,
          width: data.width || null,
          height: data.height || null,
          category: "photo",
          activity_type: data.activityType || "general",
          challenge_id: data.challengeId || null,
          speurtocht_id: data.speurtochtId || null,
          agenda_event_id: data.agendaEventId || null,
          description: data.description || null,
          tags: data.tags || null,
          location_lat: data.location?.latitude || null,
          location_lng: data.location?.longitude || null,
          location_name: data.location?.name || null,
          taken_at: data.takenAt || null,
        })
        .select("id")
        .single();

      if (dbError) throw dbError;

      setProgress(prev => prev.map(p =>
        p.photoId === photoId ? { ...p, progress: 100, status: "complete" } : p
      ));

      // Log activity
      if (userId) {
        logActivity({
          user_id: userId,
          action_type: "photo.upload",
          action_category: "photo",
          target_type: "photo",
          target_id: photoRecord.id,
          metadata: {
            weekend_id: data.weekendId,
            activity_type: data.activityType,
          },
        });
      }

      return photoRecord.id;
    } catch (err) {
      console.error("Error uploading photo:", err);
      setProgress(prev => prev.map(p =>
        p.photoId === photoId ? { ...p, status: "error", error: "Upload mislukt" } : p
      ));
      return null;
    }
  }, [userId]);

  /**
   * Upload multiple photos
   */
  const uploadMultiple = useCallback(async (photos: PhotoUploadData[]): Promise<string[]> => {
    setIsUploading(true);
    setProgress([]);

    const controller = new AbortController();
    setAbortController(controller);

    const results: string[] = [];

    for (const photo of photos) {
      if (controller.signal.aborted) break;

      const id = await uploadPhoto(photo);
      if (id) results.push(id);
    }

    setIsUploading(false);
    setAbortController(null);

    // Show success message
    if (results.length > 0) {
      Alert.alert(
        "✅ Gelukt!",
        results.length === 1
          ? "Foto is geüpload"
          : `${results.length} foto's zijn geüpload`
      );
    }

    return results;
  }, [uploadPhoto]);

  /**
   * Cancel ongoing upload
   */
  const cancelUpload = useCallback(() => {
    abortController?.abort();
    setIsUploading(false);
  }, [abortController]);

  return {
    isUploading,
    progress,
    pickPhotos,
    takePhoto,
    uploadPhoto,
    uploadMultiple,
    cancelUpload,
  };
};

/**
 * Decode base64 to Uint8Array (for Supabase upload)
 */
function decode(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}
